import TranslateIcon from "@mui/icons-material/Translate";
import { Box } from "@mui/material";
import { Button, Form, Input, Modal, Switch, message } from "antd";
import { FormInstance } from "antd/lib";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../../hooks/api";
import { useLanguages } from "../../hooks/useLanguages";
import { pathAPI } from "../../utils/pathApi";
import { fileToBlob } from "../../utils/utils";
import TranslationModal from "../common/TranslationModal";
import UploadImage from "../common/UploadImage";

interface AddChildModalProps {
  open: boolean;
  onCancel: () => void;
  mutate: () => void;
  item: CategoryChild | null;
}
const AddChildModal: React.FC<AddChildModalProps> = ({
  mutate,
  item,
  onCancel,
  ...props
}) => {
  const { t } = useTranslation();
  const formRef = useRef<FormInstance>(null);
  const [{ data: languages }] = useLanguages();
  const [serviceImage, setServiceImage] = useState<File | null>(null);

  const { id } = useParams<{ id: string }>();
  const [messageApi, contextHolder] = message.useMessage();
  const [loadingSave, setLoadingSave] = useState(false);
  const [translationModalOpen, setTranslationModalOpen] = useState(false);
  const [translations, setTranslations] = useState<any[]>([]);
  const [loadingTranslations, setLoadingTranslations] = useState(false);
  const title = item ? t("category.child.edit_title") : t("category.child.add_title");

  const deleteError = () => {
    messageApi.open({
      type: "error",
      content: t("category.messages.error"),
    });
  };

  const postChildCategory = async (data: any) => {
    if (!id) {
      message.error(t("category.child.parent_missing"));
      return;
    }

    try {
      setLoadingSave(true);
      await api.createChildCategory(id, data);
      message.success(t("category.child.created"));
      await mutate();
      handleCancel();
    } catch (error) {
      deleteError();
    } finally {
      setLoadingSave(false);
    }
  };

  const putChildCategory = async (data: any) => {
    try {
      setLoadingSave(true);
      await api.PUT(
        `${pathAPI.categoryDetail}/${id}/${item?.id}`,
        data,
        {},
        true
      );
      message.success(t("category.child.updated"));

      // Ensure we mutate (reload data) first, then close the modal
      await mutate();
      handleCancel();
    } catch (error) {
      deleteError();
    } finally {
      setLoadingSave(false);
    }
  };

  const handleCancel = () => {
    formRef.current?.resetFields();
    setServiceImage(null);
    setTranslationModalOpen(false);
    setTranslations([]);
    onCancel();
  };

  const fetchTranslations = async () => {
    if (!item?.id) return false;
    try {
      setLoadingTranslations(true);
      const response = await api.GET(`${pathAPI.categoryDetail}/${item.id}/translations`, {}, {}, true);
      setTranslations(response?.data?.translations || []);
      return true;
    } catch (error) {
      console.error("Error fetching child category translations:", error);
      message.error(t("category.translations_load_failed"));
      return false;
    } finally {
      setLoadingTranslations(false);
    }
  };

  const handleOpenTranslationModal = async () => {
    const loaded = await fetchTranslations();
    if (loaded) {
      setTranslationModalOpen(true);
    }
  };

  const handleTranslationSuccess = async () => {
    await fetchTranslations();
    await mutate();
  };

  // Initialize form when modal opens or item changes
  useEffect(() => {
    if (props.open) {
      // Always reset form when modal opens
      formRef.current?.resetFields();
      setServiceImage(null);

      // Clear initial values and images when adding new
      if (!item) {
        formRef.current?.setFieldsValue({
          name: "",
          feature: false,
          active: false,
        });
      }
      // If we have an item (edit mode), set the values
      else {
        const initialValues = {
          name: item.name || "",
          feature: item.feature || false,
          active: item.active || false,
        };

        // Set form values (with short delay to ensure form is ready)
        setTimeout(() => {
          formRef.current?.setFieldsValue(initialValues);
        }, 100);
      }
    }
  }, [props.open, item]);

  const currentName = item ? item.name || "" : "";
  const currentFeature = item ? item.feature || false : false;
  const currentActive = item ? item.active || false : false;

  const onFinish = async (values: any) => {
    try {
      const formData = new FormData();
      // Handle image uploads
      if (serviceImage) {
        const blob = await fileToBlob(serviceImage);
        formData.append(
          "serviceImage",
          blob as Blob,
          serviceImage.name || "default-filename.png"
        );
      }
      // Add basic fields to formData - ensure name is always included
      const categoryName = values.name?.trim() || "";
      if (!categoryName) {
        message.error(t("category.child.name_required"));
        return;
      }

      formData.append("name", categoryName);
      formData.append("isFeature", `${values?.feature ? 1 : 0}`);
      formData.append("active", `${values?.active ? 1 : 0}`);

      // Process create or update
      if (item) {
        await putChildCategory(formData);
      } else {
        await postChildCategory(formData);
      }
    } catch (error) {
      console.error("Error in onFinish:", error);
      message.error(t("category.child.save_failed"));
      setLoadingSave(false);
    }
  };

  return (
    <Modal
      onCancel={handleCancel}
      {...props}
      destroyOnClose={true}
      width={720}
      style={{ top: 40 }}
      styles={{
        content: {
          borderRadius: "12px",
          overflow: "hidden",
          padding: 0,
        },
      }}
      footer={null}
    >
      <Box>
        <Box
          bgcolor={"#ba2156"}
          color={"#fff"}
          px={3}
          py={2}
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
          gap={2}
        >
          <Box>
            <Box fontSize={"18px"} fontWeight={700} lineHeight={1.3}>
              {title}
            </Box>
            <Box fontSize={"12px"} sx={{ opacity: 0.85 }}>
              {t("category.child.subtitle")}
            </Box>
          </Box>
        </Box>

        <Box px={3} py={3}>
          <Form
            onFinish={onFinish}
            ref={formRef}
            layout="vertical"
            initialValues={{
              name: currentName,
              feature: currentFeature,
              active: currentActive,
            }}
          >
            <Box
              display={"flex"}
              gap={3}
              alignItems={"flex-start"}
              sx={{
                "@media (max-width: 575px)": { flexDirection: "column" },
              }}
            >
              <Box
                flex={"0 0 240px"}
                width={"240px"}
                sx={{ "@media (max-width: 575px)": { width: "100%" } }}
              >
                <Form.Item
                  name="serviceImage"
                  label={t("category.child.service_image")}
                  rules={[
                    {
                      validator: () => {
                        if (!item && !serviceImage) {
                          return Promise.reject(
                            new Error(t("category.child.service_image_required"))
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <UploadImage
                    width={"100%"}
                    urlImage={item?.service_image || item?.active_image || ""}
                    height={"240px"}
                    onChangeFile={setServiceImage}
                  />
                </Form.Item>
              </Box>

              <Box flex={1} minWidth={0} width={"100%"}>
                <Form.Item
                  name="name"
                  label={t("category.child.name")}
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: t("category.child.name_required"),
                    },
                  ]}
                >
                  <Input
                    allowClear
                    placeholder={t("category.child.name_placeholder")}
                    style={{ height: "40px" }}
                  />
                </Form.Item>

                {item?.id && (
                  <Button
                    icon={<TranslateIcon style={{ fontSize: "16px" }} />}
                    onClick={handleOpenTranslationModal}
                    loading={loadingTranslations}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      marginBottom: 16,
                      color: "#ba2156",
                      borderColor: "#ba2156",
                    }}
                  >
                    {t("category.manage_translations")}
                  </Button>
                )}

                <Box
                  border={"1px solid var(--app-border-light)"}
                  borderRadius={"8px"}
                  overflow={"hidden"}
                >
                  {[
                    {
                      name: "active",
                      label: t("category.child.active_label"),
                      desc: t("category.child.active_desc"),
                    },
                    {
                      name: "feature",
                      label: t("category.child.feature_label"),
                      desc: t("category.child.feature_desc"),
                    },
                  ].map((row, index) => (
                    <Box
                      key={row.name}
                      px={2}
                      py={1.5}
                      display={"flex"}
                      alignItems={"center"}
                      justifyContent={"space-between"}
                      gap={2}
                      borderTop={index ? "1px solid var(--app-border-light)" : "none"}
                    >
                      <Box>
                        <Box fontSize={"14px"} fontWeight={500}>
                          {row.label}
                        </Box>
                        <Box fontSize={"12px"} color={"#595959"}>
                          {row.desc}
                        </Box>
                      </Box>
                      <Form.Item
                        name={row.name}
                        valuePropName="checked"
                        style={{ margin: 0 }}
                      >
                        <Switch
                          checkedChildren={t("category.switch.on")}
                          unCheckedChildren={t("category.switch.off")}
                        />
                      </Form.Item>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            <Box
              display={"flex"}
              width={"100%"}
              mt={4}
              pt={2}
              borderTop={"1px solid var(--app-border-light)"}
              justifyContent={"flex-end"}
              gap={2}
              sx={{
                "@media (max-width: 575px)": {
                  flexDirection: "column-reverse",
                  "& button": {
                    width: "100% !important",
                  },
                },
              }}
            >
              <Button
                size="middle"
                style={{ width: "120px" }}
                onClick={handleCancel}
                disabled={loadingSave}
              >
                {t("btn.cancel")}
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                style={{
                  width: "120px",
                  backgroundColor: "#ba2156",
                  border: "none",
                }}
                loading={loadingSave}
                disabled={loadingSave}
              >
                {loadingSave ? t("category.detail.saving") : t("btn.ok")}
              </Button>
            </Box>
          </Form>
        </Box>
      </Box>
      {contextHolder}
      {item?.id && (
        <TranslationModal
          open={translationModalOpen}
          onCancel={() => setTranslationModalOpen(false)}
          onSuccess={handleTranslationSuccess}
          entityId={item.id}
          entityType="category"
          translations={translations}
          languages={languages || []}
          title={t("category.children_translations_title")}
          entityName={item.name}
        />
      )}
    </Modal>
  );
};

export default AddChildModal;
